const DFLOW_BASE_URL = 'https://prediction-markets-api.dflow.net/api/v1'

// Build headers for dflow API requests (includes API key if configured)
export function getDflowHeaders(): HeadersInit {
	const headers: HeadersInit = {
		Accept: 'application/json',
	}
	if (process.env.DFLOW_API_KEY) {
		headers['x-api-key'] = process.env.DFLOW_API_KEY
	}
	return headers
}

// Raw API response types
export type DflowMarketAccount = {
	marketLedger: string
	yesMint: string
	noMint: string
	isInitialized: boolean
	redemptionStatus: string | null
}

export type DflowMarket = {
	ticker: string
	eventTicker: string
	marketType: 'binary'
	title: string
	subtitle: string
	yesSubTitle: string
	noSubTitle: string
	openTime: number
	closeTime: number
	expirationTime: number
	// Kalshi market statuses:
	// - initialized: Market created but not yet active
	// - active: Market is open for trading
	// - inactive: Market exists but trading is paused
	// - closed: Market is closed, awaiting settlement
	// - determined/settled/finalized: Market has been settled with a result
	status: 'active' | 'initialized' | 'inactive' | 'closed' | 'determined' | 'settled' | 'finalized'
	volume: number
	result: string
	openInterest: number
	canCloseEarly: boolean
	earlyCloseCondition: string
	rulesPrimary: string
	rulesSecondary: string
	yesBid: string | null
	yesAsk: string | null
	noBid: string | null
	noAsk: string | null
	accounts: Record<string, DflowMarketAccount>
}

export type DflowEvent = {
	ticker: string
	seriesTicker: string
	strikeDate: string | null
	strikePeriod: string | null
	title: string
	subtitle: string
	imageUrl: string
	competition: string | null
	competitionScope: string | null
	settlementSources: Array<{ name: string; url: string }>
	volume: number
	volume24h: number
	liquidity: number
	openInterest: number
	markets?: DflowMarket[]
}

export type DflowEventsResponse = {
	events: DflowEvent[]
	cursor?: string
}

export type DflowMarketsResponse = {
	markets: DflowMarket[]
	cursor?: string
}

// Kalshi status values for filtering
export type KalshiStatusFilter = 'initialized' | 'active' | 'inactive' | 'closed' | 'determined'

// Fetch events with nested markets
export async function fetchDflowEvents(params?: {
	status?: 'active'
	seriesTickers?: string
	limit?: number
	cursor?: string
	withNestedMarkets?: boolean
	sort?: string // volume, volume24h, liquidity, openInterest, startDate
	ascending?: boolean
	isInitialized?: boolean
}): Promise<DflowEventsResponse> {
	const searchParams = new URLSearchParams()

	// Status filter - can be single value or comma-separated list
	if (params?.status === 'active') {
		searchParams.set('status', params.status)
	}
	if (params?.seriesTickers) {
		searchParams.set('seriesTickers', params.seriesTickers)
	}
	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.withNestedMarkets !== false) {
		searchParams.set('withNestedMarkets', 'true')
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}
	if (params?.ascending) {
		searchParams.set('ascending', 'true')
	}
	// Filter by onchain tradability (isInitialized)
	if (params?.isInitialized !== undefined) {
		searchParams.set('isInitialized', params.isInitialized.toString())
	}

	const url = `${DFLOW_BASE_URL}/events?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch markets directly (flat list, not grouped by event)
export async function fetchDflowMarkets(params?: {
	status?: KalshiStatusFilter[] | string
	limit?: number
	cursor?: string
	sort?: string // volume, volume24h, liquidity, openInterest, startDate
	ascending?: boolean
	isInitialized?: boolean
}): Promise<DflowMarketsResponse> {
	const searchParams = new URLSearchParams()

	// Status filter - can be single value or comma-separated list
	if (params?.status === 'active') {
		searchParams.set('status', params.status)
	}
	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}
	if (params?.ascending) {
		searchParams.set('ascending', 'true')
	}
	// Filter by onchain tradability (isInitialized)
	if (params?.isInitialized !== undefined) {
		searchParams.set('isInitialized', params.isInitialized.toString())
	}

	const url = `${DFLOW_BASE_URL}/markets?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch single market by ticker
export async function fetchDflowMarket(ticker: string): Promise<DflowMarket> {
	const url = `${DFLOW_BASE_URL}/market/${ticker}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch market by mint address
export async function fetchDflowMarketByMint(mint: string): Promise<DflowMarket> {
	const url = `${DFLOW_BASE_URL}/market/by-mint/${mint}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch single event
export async function fetchDflowEvent(ticker: string): Promise<DflowEvent> {
	const url = `${DFLOW_BASE_URL}/event/${ticker}?withNestedMarkets=true`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Search endpoint (supports q param)
export async function fetchDflowSearch(params?: {
	q: string
	limit?: number
	cursor?: string
	withNestedMarkets?: boolean
	sort?: string
}): Promise<DflowEventsResponse> {
	const searchParams = new URLSearchParams()

	if (!params?.q) {
		throw new Error('dflow search requires q parameter')
	}

	searchParams.set('q', params.q)

	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.withNestedMarkets !== false) {
		searchParams.set('withNestedMarkets', 'true')
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}

	const url = `${DFLOW_BASE_URL}/search?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}
